import React, { type ComponentType, useMemo, memo, useState, useCallback } from 'react';
// @ts-expect-error - TS2724 - '"react-beautiful-dnd"' has no exported member named 'DragHandleProps'. Did you mean 'DraggableProps'?
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DragHandleProps } from 'react-beautiful-dnd';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import CollapseIcon from '@atlaskit/icon/glyph/editor/collapse';
import ExpandIcon from '@atlaskit/icon/glyph/editor/expand';
import InlineDialog from '@atlaskit/inline-dialog';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';
import { AvatarLite } from '@atlassian/jira-business-avatar-lite';
import { CategoryTag } from '@atlassian/jira-business-categories';
import { ConditionalWrapper } from '@atlassian/jira-business-conditional-wrapper';
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CATEGORY,
	GROUP_BY_PRIORITY,
	GROUP_BY_STATUS,
} from '@atlassian/jira-business-constants';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	isAutomationDiscoveryOnBoardColumnsExperimentEnabled,
	isInAutomationDiscoveryOnBoardColumnsExperimentVariationCohort,
} from '@atlassian/jira-business-feature-flags';
import StatusLozenge from '@atlassian/jira-common-components-status-lozenge';
import {
	statusCategoryForId,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories';
import { useIntl } from '@atlassian/jira-intl';
import {
	AutomationDiscoveryModal,
	useAutomationDiscoveryContext,
	useJwmAutomationDiscoveryOnBoardColumnsExperiment,
} from '@atlassian/jira-moon-experiment-automation-discovery-on-board-columns';
import { useBoardHasScrollTop } from '@atlassian/jira-platform-board-kit/src/controllers/board-scroll';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { NULL_GROUP_KEY } from '../../../../common/constants';
import type { BoardColumn } from '../../../../common/types';
import { ContactAdminToModifyBoardMessage } from '../../../../common/ui/contact-admin-to-modify-board';
import { OpenProjectSettingsExceededIssueTypes } from '../../../../common/ui/open-project-settings-exceeded-issue-types';
import { OpenProjectSettingsMultipleWorkflows } from '../../../../common/ui/open-project-settings-multiple-workflows';
import {
	useRenameColumn,
	useDetectMultipleWorkflows,
} from '../../../../controllers/add-rename-delete-status-old';
import { useCardTransitionsOld } from '../../../../controllers/card-transitions-old';
import { useCollapseColumn } from '../../../../controllers/collapse-column-old';
import { matchConfettiEmoji } from '../../../../controllers/column-confetti-old/utils';
import { useGroupBy } from '../../../../controllers/group-by';
import {
	useAreAnyColumnsUpdating,
	useBoardStoreState,
	useHasMultipleTransitionsBetweenStatuses,
} from '../../../../services/board-store';
// eslint-disable-next-line jira/import/no-parent-imports
import headerContentMessages from '../../../board/column/column-header/header-content/messages';
// eslint-disable-next-line jira/import/no-parent-imports
import messages from '../../../board/column/column-header/messages';
import { BoardConfettiAsync } from './board-confetti';
import EditableColumnHeader from './editable-column-header';
import HeaderDropdown from './header-dropdown';
import {
	Container,
	HeaderTitleWrapper,
	EditableColumnHeaderWrapper,
	TextWrapper,
	TransitionInfoContainer,
	TitleReadViewWrapper,
	RenameClickNotAllowedWrapper,
	HeaderLeftSideWrapper,
	MenuWrapper,
	ColumnWrapper,
	CollapseButtonWrapper,
	ExpandButtonWrapper,
	Image,
	TitleReadOnlyWrapper,
} from './styled';

// @ts-expect-error - TS7006 - Parameter 'transition' implicitly has an 'any' type. | TS7006 - Parameter 'sourceColumn' implicitly has an 'any' type. | TS7006 - Parameter 'targetColumn' implicitly has an 'any' type.
const getTransitionInfoOverlay = (transition, sourceColumn, targetColumn) => {
	const sourceStatus = {
		id: Number(sourceColumn.statusId),
		name: sourceColumn.name,

		// @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Readonly<{ undefined: 1; new: 2; done: 3; indeterminate: 4; }>'.
		category: statusCategoryForId(StatusCategoryIds[sourceColumn.statusCategory]),
	};
	return (
		<TransitionInfoContainer>
			{transition?.isGlobal && sourceStatus && sourceStatus.category ? (
				<StatusLozenge category={sourceStatus.category} name={sourceStatus.name} />
			) : (
				<StatusLozenge name={sourceColumn.name} category={sourceStatus.category} />
			)}
			<ArrowRightIcon size="small" label="" />
			<StatusLozenge
				// @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Readonly<{ undefined: 1; new: 2; done: 3; indeterminate: 4; }>'.
				category={statusCategoryForId(StatusCategoryIds[targetColumn.statusCategory])}
				name={targetColumn.name}
			/>
		</TransitionInfoContainer>
	);
};

export type Props = {
	isClone?: boolean;
	isDropDisabled: boolean;
	isDeletableColumn: boolean;
	column: BoardColumn;
	// this prop is coming from board-kit using clone in src/packages/platform/ui/board-kit/src/ui/column/draggable-column/column/index.js
	akDragHandleProps?: DragHandleProps;
};

const ColumnHeaderWithPivotSupport: ComponentType<Props> = memo(
	// eslint-disable-next-line complexity
	({ column, isClone, akDragHandleProps, isDeletableColumn, isDropDisabled, ...other }: Props) => {
		const { formatMessage } = useIntl();
		const { transition, sourceColumn } = useCardTransitionsOld();
		const [hasScrollTop] = useBoardHasScrollTop();
		const [hasMultipleTransitions] = useHasMultipleTransitionsBetweenStatuses();

		const [groupBy] = useGroupBy();
		const isGroupedByStatus = groupBy === GROUP_BY_STATUS;

		const { cardDragState } = useBoardStoreState();

		const { shouldRenderExperiment: automationDiscoveryEnabled } =
			isAutomationDiscoveryOnBoardColumnsExperimentEnabled()
				? // eslint-disable-next-line react-hooks/rules-of-hooks
					useJwmAutomationDiscoveryOnBoardColumnsExperiment()
				: { shouldRenderExperiment: false };

		const { isAutomationDiscoveryModalOpen, setIsAutomationDiscoveryModalOpen, isTargetColumn } =
			automationDiscoveryEnabled
				? // eslint-disable-next-line react-hooks/rules-of-hooks
					useAutomationDiscoveryContext()
				: {
						isTargetColumn: () => false,
						isAutomationDiscoveryModalOpen: false,
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						setIsAutomationDiscoveryModalOpen: () => {},
					};

		const isDraggingCardOver = useMemo(
			() => String(cardDragState.columnId) === String(column.id),
			[cardDragState, column.id],
		);

		const oldShouldShowTransitionInfo =
			column.groupKey == null && transition !== undefined && !!sourceColumn;

		const shouldShowTransitionInfo =
			oldShouldShowTransitionInfo && isDraggingCardOver && !column.isCollapsed;

		const { collapseColumn, expandColumn } = useCollapseColumn({
			groupKey: column.groupKey,
		});
		const { renameColumn, validateRename } = useRenameColumn();
		const [areAnyColumnsUpdating] = useAreAnyColumnsUpdating();

		const [isEditMode, setEditMode] = useState(false);
		const [isCannotRenameDialogOpen, setIsCannotRenameDialogOpen] = useState(false);
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const project = useProject();
		const isTeamManaged = project.isSimplified;
		const isProjectAdmin = project.permissions.administerProject;

		const {
			data: { hasMultipleWorkflows, hasExceededMaxIssueTypes } = {},
			loading: multipleWorkflowsLoading = false,
			error: multipleWorkflowsError,
		} = useDetectMultipleWorkflows({
			shouldFetchOnMount: isProjectAdmin && isTeamManaged,
		});

		const isColumnDraggable = isProjectAdmin && !isClone;
		const isColumnCollapsed = !!column.isCollapsed;

		const isCollapsedColumnHighlighted = useMemo(
			() =>
				isColumnCollapsed &&
				!isDropDisabled &&
				isDraggingCardOver &&
				(!hasMultipleTransitions || !isGroupedByStatus),
			[
				isColumnCollapsed,
				isDropDisabled,
				isDraggingCardOver,
				hasMultipleTransitions,
				isGroupedByStatus,
			],
		);

		const showConfetti = useMemo(
			() => (column.isDone || matchConfettiEmoji(column.name)) && !column.isCollapsed,
			[column],
		);

		const displayDialogueMessage = useCallback(() => {
			if (!isProjectAdmin) {
				return <ContactAdminToModifyBoardMessage />;
			}
			if (hasExceededMaxIssueTypes) {
				return <OpenProjectSettingsExceededIssueTypes />;
			}

			return <OpenProjectSettingsMultipleWorkflows />;
		}, [isProjectAdmin, hasExceededMaxIssueTypes]);

		// CMP should only show readOnly
		// or there is a failure fetching multiple workflows status or it's still loading
		// or non status pivot mode
		// or any columns updating
		const shouldRenderReadOnly =
			!isTeamManaged ||
			multipleWorkflowsLoading ||
			multipleWorkflowsError != null ||
			(column.groupBy != null && column.groupBy !== GROUP_BY_STATUS) ||
			areAnyColumnsUpdating;

		const renderHeaderContent = () => {
			const headerContent = (
				<>
					{
						{
							[GROUP_BY_STATUS]: (
								<StatusLozenge
									name={column.name}
									category={statusCategoryForId(
										StatusCategoryIds[
											// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
											column.statusCategory as keyof typeof StatusCategoryIds
										],
									)}
								/>
							),
							[GROUP_BY_PRIORITY]: (
								<>
									<Tooltip content={column.name}>
										{column.imageUrl && <Image alt={column.name} src={column.imageUrl} />}
									</Tooltip>
									<TextWrapper isBold isTruncated isCapitalised>
										{column.groupKey === NULL_GROUP_KEY
											? formatMessage(headerContentMessages.noPriority)
											: column.name}
									</TextWrapper>
								</>
							),
							[GROUP_BY_ASSIGNEE]: (
								<>
									{column.groupKey === NULL_GROUP_KEY ? (
										<Avatar appearance="circle" src="" size="small" />
									) : (
										<AvatarLite avatarUrl={column.imageUrl ?? ''} name={column.name ?? ''} />
									)}
									<TextWrapper isBold isTruncated isCapitalised>
										{column.groupKey === NULL_GROUP_KEY
											? formatMessage(headerContentMessages.noAssignee)
											: column.name}
									</TextWrapper>
								</>
							),
							[GROUP_BY_CATEGORY]: (
								<>
									<CategoryTag
										categoryName={
											column.groupKey === NULL_GROUP_KEY
												? formatMessage(headerContentMessages.noCategory)
												: column.name
										}
										categoryColor={column.color}
									/>
								</>
							),
						}[column.groupBy ?? GROUP_BY_STATUS]
					}
					<TextWrapper isBold={false}>{column.issues.length}</TextWrapper>
				</>
			);

			const readViewHeaderRender = (
				<TitleReadViewWrapper isLoading={!!column?.loading} isColumnCollapsed={isColumnCollapsed}>
					{headerContent}
				</TitleReadViewWrapper>
			);

			// collapsed column should render read only
			if (shouldRenderReadOnly || isColumnCollapsed) {
				return (
					<TitleReadOnlyWrapper data-testid="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.title-read-view-wrapper">
						{readViewHeaderRender}
					</TitleReadOnlyWrapper>
				);
			}

			// for TMP end user that does not have modify permission
			if (!isProjectAdmin || hasMultipleWorkflows || hasExceededMaxIssueTypes) {
				return (
					<InlineDialog
						content={
							<RenameClickNotAllowedWrapper>
								{displayDialogueMessage()}
							</RenameClickNotAllowedWrapper>
						}
						isOpen={isCannotRenameDialogOpen}
						onClose={() => {
							setIsCannotRenameDialogOpen(false);
						}}
					>
						<TitleReadViewWrapper
							onMouseUp={() => {
								fireUIAnalytics(
									createAnalyticsEvent({}),
									'columnHeader clicked',
									'jwmBoardColumnRenameFromTitle',
									{
										isProjectAdmin,
										hasMultipleWorkflows,
										hasExceededMaxIssueTypes,
									},
								);
								setIsCannotRenameDialogOpen(true);
							}}
							isLoading={!!column?.loading}
							showDisabledCursor={isCannotRenameDialogOpen}
							data-testid="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.title-read-view-wrapper"
						>
							{headerContent}
						</TitleReadViewWrapper>
					</InlineDialog>
				);
			}

			return (
				<EditableColumnHeaderWrapper data-testid="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.editable-column-header-wrapper">
					<EditableColumnHeader
						fallback={readViewHeaderRender}
						defaultValue={column.name}
						isEditing={isEditMode}
						readView={() => (
							<TitleReadViewWrapper isLoading={!!column?.loading}>
								{headerContent}
							</TitleReadViewWrapper>
						)}
						onCancel={() => setEditMode(false)}
						onConfirm={(newName) => {
							setEditMode(false);
							renameColumn({
								oldName: column.name,
								newName,
								statusId: column.statusId,
							});

							fireUIAnalytics(
								createAnalyticsEvent({}),
								'columnRenameInlineEdit confirmed',
								'jwmBoardColumnRename',
							);
						}}
						onEdit={() => {
							setEditMode(true);

							fireUIAnalytics(
								createAnalyticsEvent({}),
								'columnHeader clicked',
								'jwmBoardColumnRenameFromTitle',
								{
									isProjectAdmin,
									hasMultipleWorkflows,
									hasExceededMaxIssueTypes,
								},
							);
						}}
						onValidate={(newName) => validateRename({ oldName: column.name, newName })}
					/>
				</EditableColumnHeaderWrapper>
			);
		};

		const renderMenu = (isTeamManaged && !isColumnCollapsed) || !column.isCollapsed;

		return (
			<Container
				data-testid="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.container"
				isDraggable
				isShadowVisible={hasScrollTop}
				isColumnCollapsed={isColumnCollapsed}
				isCollapsedColumnHighlighted={isCollapsedColumnHighlighted}
				{...other}
			>
				{isInAutomationDiscoveryOnBoardColumnsExperimentVariationCohort() &&
					isTargetColumn(Number(column.id)) && (
						<AutomationDiscoveryModal
							isOpen={isAutomationDiscoveryModalOpen}
							setIsOpen={setIsAutomationDiscoveryModalOpen}
						/>
					)}
				<HeaderTitleWrapper
					{...akDragHandleProps}
					isDeletableColumn={isDeletableColumn}
					isColumnCollapsed={isColumnCollapsed}
					isCollapsedColumnHighlighted={isCollapsedColumnHighlighted}
					onClick={isColumnCollapsed ? expandColumn : undefined}
					data-testid="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.header-title-wrapper"
				>
					<ConditionalWrapper
						condition={isColumnDraggable}
						renderWrapper={(children) => (
							<ColumnWrapper isColumnCollapsed={isColumnCollapsed}>{children}</ColumnWrapper>
						)}
					>
						<HeaderLeftSideWrapper
							data-testid="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.header-left-side-wrapper"
							isColumnCollapsed={isColumnCollapsed}
						>
							{shouldShowTransitionInfo && sourceColumn != null
								? getTransitionInfoOverlay(transition, sourceColumn, column)
								: renderHeaderContent()}
						</HeaderLeftSideWrapper>

						{isColumnCollapsed && (
							<ExpandButtonWrapper data-component-selector="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.expand-button-wrapper">
								<Button
									testId="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.expand-column"
									spacing="none"
									appearance="subtle"
									iconBefore={
										<ExpandIcon label={formatMessage(messages.expandButton)} size="medium" />
									}
									// @ts-expect-error - TS2322 - Type '(event?: MouseEvent | undefined) => void' is not assignable to type '(e: MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => void'.
									onClick={expandColumn}
								/>
							</ExpandButtonWrapper>
						)}
						{renderMenu && (
							<MenuWrapper>
								<CollapseButtonWrapper data-component-selector="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.collapse-button-wrapper">
									<Button
										testId="work-management-board.ui.board-old.column-old.column-header-with-pivot-support-old.collapse-column"
										appearance="subtle"
										iconBefore={
											<CollapseIcon label={formatMessage(messages.collapseButton)} size="medium" />
										}
										onClick={collapseColumn}
									/>
								</CollapseButtonWrapper>
								{!shouldRenderReadOnly && (
									<>
										{column?.loading === true ? (
											<Spinner size="small" />
										) : (
											<HeaderDropdown
												hasMultipleWorkflows={hasMultipleWorkflows}
												hasExceededMaxIssueTypes={hasExceededMaxIssueTypes}
												column={column}
												onRenameClick={() => {
													setEditMode(true);
												}}
											/>
										)}
									</>
								)}
							</MenuWrapper>
						)}
					</ConditionalWrapper>
					{showConfetti && <BoardConfettiAsync columnId={Number(column.id)} />}
				</HeaderTitleWrapper>
			</Container>
		);
	},
);

export default ColumnHeaderWithPivotSupport;
